import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Truck, CheckCircle2, AlertTriangle, MapPin, Clock, Phone, Ruler, ShieldCheck, ArrowRight, Droplets, Sprout, CalendarDays } from 'lucide-react';
import { Link } from 'react-router-dom';
export function DeliveryPrep() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeIn = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    viewport: {
      once: true
    },
    transition: {
      duration: 0.5
    }
  };
  const stagger = {
    initial: {
      opacity: 0
    },
    whileInView: {
      opacity: 1
    },
    viewport: {
      once: true
    },
    transition: {
      staggerChildren: 0.1
    }
  };
  const item = {
    initial: {
      opacity: 0,
      y: 20
    },
    whileInView: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.5
    }
  };
  return <div className="pt-24 pb-16 bg-[#fdfbf7] dark:bg-[#2e2e2e] min-h-screen" data-id="element-1865">
      {/* Hero Section */}
      <section className="relative py-20 bg-[#3d3a3a] dark:bg-[#3d3a3a] overflow-hidden border-b border-stone-200 dark:border-white/15" data-id="element-1866">
        <div className="absolute top-0 left-0 right-0 h-24 bg-gradient-to-b from-[#353232]/50 to-transparent pointer-events-none" data-id="element-1867" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-1868">
          <motion.div initial={{
          scale: 0.9,
          opacity: 0
        }} animate={{
          scale: 1,
          opacity: 1
        }} transition={{
          type: 'spring',
          damping: 20,
          stiffness: 200
        }} className="w-20 h-20 bg-brand-orange/10 rounded-full flex items-center justify-center mx-auto mb-6 border border-brand-orange/20" data-id="element-1869">
            <Truck className="w-10 h-10 text-brand-orange" data-id="element-1870" />
          </motion.div>
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.5
        }} className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-white tracking-tight mb-6" data-id="element-1871">
            Prepare for Your <span className="text-brand-orange" data-id="element-1872">Delivery</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.2,
          duration: 0.5
        }} className="text-lg md:text-xl text-white/70 max-w-2xl mx-auto font-medium" data-id="element-1873">
            Follow this simple guide to ensure a smooth, safe, and successful
            material delivery to your home or job site.
          </motion.p>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-16" data-id="element-1874">
        {/* Before Delivery Day */}
        <motion.section {...fadeIn} data-id="element-1875">
          <div className="flex items-center gap-4 mb-8" data-id="element-1876">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20" data-id="element-1877">
              <CalendarDays className="w-6 h-6 text-brand-orange" data-id="element-1878" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1879">
              Before Delivery Day
            </h2>
          </div>
          <motion.div variants={stagger} initial="initial" whileInView="whileInView" viewport={{
          once: true
        }} className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1880">
            {[{
            icon: <Truck className="w-5 h-5" data-id="element-1881" />,
            text: 'Clear the dump area of vehicles, toys, and obstacles'
          }, {
            icon: <MapPin className="w-5 h-5" data-id="element-1882" />,
            text: 'Mark the exact spot where you want material dumped (use a cone, tarp, or flag)'
          }, {
            icon: <ShieldCheck className="w-5 h-5" data-id="element-1883" />,
            text: 'Ensure gate access if fenced and provide gate codes if applicable'
          }, {
            icon: <Ruler className="w-5 h-5" data-id="element-1884" />,
            text: 'Measure clearance: Trucks are 10ft wide, 12ft tall, and 30ft long'
          }, {
            icon: <AlertTriangle className="w-5 h-5" data-id="element-1885" />,
            text: 'Check for low-hanging overhead wires or tree branches'
          }, {
            icon: <CheckCircle2 className="w-5 h-5" data-id="element-1886" />,
            text: 'Confirm someone 18+ will be on-site to receive the delivery'
          }].map((point, i) => <motion.div key={i} variants={item} className="flex items-start gap-3 p-4 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm" data-id="element-1887">
                <div className="text-brand-orange shrink-0 mt-0.5" data-id="element-1888">
                  {point.icon}
                </div>
                <p className="text-brand-charcoal dark:text-stone-200 font-medium" data-id="element-1889">
                  {point.text}
                </p>
              </motion.div>)}
          </motion.div>
        </motion.section>

        {/* Day of Delivery */}
        <motion.section {...fadeIn} data-id="element-1890">
          <div className="flex items-center gap-4 mb-8" data-id="element-1891">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20" data-id="element-1892">
              <Clock className="w-6 h-6 text-brand-orange" data-id="element-1893" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1894">
              Day of Delivery
            </h2>
          </div>
          <div className="bg-white dark:bg-[#353535] rounded-2xl border border-stone-200 dark:border-white/15 shadow-sm overflow-hidden" data-id="element-1895">
            <div className="divide-y divide-stone-200 dark:divide-white/15" data-id="element-1896">
              {[{
              icon: <Phone className="w-6 h-6" data-id="element-1897" />,
              title: 'Driver will call',
              desc: 'Expect a call 15-30 minutes before the driver arrives at your location.'
            }, {
              icon: <MapPin className="w-6 h-6" data-id="element-1898" />,
              title: 'Point the way',
              desc: 'Direct the driver to your marked dump spot upon arrival.'
            }, {
              icon: <AlertTriangle className="w-6 h-6" data-id="element-1899" />,
              title: 'Stay clear',
              desc: 'Keep children, pets, and yourself at a safe distance during the dumping process.'
            }, {
              icon: <CheckCircle2 className="w-6 h-6" data-id="element-1900" />,
              title: 'Inspect material',
              desc: 'Verify the material is correct before the driver leaves the site.'
            }].map((step, i) => <div key={i} className="p-6 flex items-start gap-4" data-id="element-1901">
                  <div className="w-10 h-10 rounded-full bg-brand-orange/10 dark:bg-brand-orange/20 flex items-center justify-center text-brand-orange shrink-0" data-id="element-1902">
                    {step.icon}
                  </div>
                  <div data-id="element-1903">
                    <h3 className="text-lg font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-1904">
                      {step.title}
                    </h3>
                    <p className="text-stone-700 dark:text-stone-400 font-medium" data-id="element-1905">
                      {step.desc}
                    </p>
                  </div>
                </div>)}
            </div>
          </div>
        </motion.section>

        {/* Access Tips */}
        <motion.section {...fadeIn} data-id="element-1906">
          <div className="flex items-center gap-4 mb-8" data-id="element-1907">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20" data-id="element-1908">
              <Truck className="w-6 h-6 text-brand-orange" data-id="element-1909" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1910">
              Access Tips
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-1911">
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm" data-id="element-1912">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1913">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-1914" />
                Firm Ground Required
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-1915">
                Fully loaded trucks are extremely heavy. They need firm ground
                or pavement to drive on, not soggy lawns or soft dirt, to avoid
                getting stuck or causing damage.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm" data-id="element-1916">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1917">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-1918" />
                Steep Driveways
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-1919">
                Very steep driveways may be unsafe for a dump truck to navigate
                or dump on. In these cases, a street-level dump may be required.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm" data-id="element-1920">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1921">
                <AlertTriangle className="w-5 h-5 text-brand-orange" data-id="element-1922" />
                Cul-de-sacs & Tight Turns
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-1923">
                Large dump trucks need significant turning radius. Ensure there
                is adequate room to maneuver in cul-de-sacs or tight
                neighborhoods.
              </p>
            </div>
            <div className="p-6 bg-brand-orange/10 dark:bg-brand-orange/10 rounded-xl border border-brand-orange/30 shadow-sm flex flex-col justify-center" data-id="element-1924">
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2 flex items-center gap-2" data-id="element-1925">
                <Phone className="w-5 h-5 text-brand-orange" data-id="element-1926" />
                Unsure about access?
              </h3>
              <p className="text-stone-700 dark:text-stone-300 text-sm font-medium mb-3" data-id="element-1927">
                If you have concerns about your delivery location, call us first
                and we can review a photo of your site.
              </p>
              <a href="tel:206-619-3742" className="inline-flex items-center text-brand-orange font-bold hover:underline" data-id="element-1928">
                Call (206) 619-3742 <ArrowRight className="w-4 h-4 ml-1" data-id="element-1929" />
              </a>
            </div>
          </div>
        </motion.section>

        {/* After Delivery */}
        <motion.section {...fadeIn} data-id="element-1930">
          <div className="flex items-center gap-4 mb-8" data-id="element-1931">
            <div className="w-12 h-12 bg-brand-orange/10 rounded-xl flex items-center justify-center border border-brand-orange/20" data-id="element-1932">
              <Sprout className="w-6 h-6 text-brand-orange" data-id="element-1933" />
            </div>
            <h2 className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-1934">
              After Delivery
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1935">
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm text-center" data-id="element-1936">
              <Clock className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-1937" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1938">
                Spread Quickly
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-1939">
                Spread material within 48 hours to avoid settling and compaction
                in the pile.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm text-center" data-id="element-1940">
              <Droplets className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-1941" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1942">
                Wet Before Compacting
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-1943">
                Lightly wet gravel before using a plate compactor for maximum
                density and stability.
              </p>
            </div>
            <div className="p-6 bg-white dark:bg-[#353535] rounded-xl border border-stone-200 dark:border-white/15 shadow-sm text-center" data-id="element-1944">
              <Sprout className="w-8 h-8 text-brand-orange mx-auto mb-3" data-id="element-1945" />
              <h3 className="font-bold text-brand-charcoal dark:text-white mb-2" data-id="element-1946">
                Protect Trees
              </h3>
              <p className="text-stone-700 dark:text-stone-400 text-sm font-medium" data-id="element-1947">
                When spreading bark or mulch, keep it at least 3 inches away
                from tree trunks to prevent rot.
              </p>
            </div>
          </div>
        </motion.section>

        {/* CTA */}
        <motion.section {...fadeIn} className="bg-brand-charcoal dark:bg-[#282828] rounded-2xl p-8 md:p-12 text-center relative overflow-hidden" data-id="element-1948">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-5 pointer-events-none" data-id="element-1949"></div>
          <div className="relative z-10" data-id="element-1950">
            <h2 className="font-heading text-3xl md:text-4xl font-extrabold text-white mb-4" data-id="element-1951">
              Ready to order?
            </h2>
            <p className="text-stone-300 text-lg mb-8 max-w-xl mx-auto font-medium" data-id="element-1952">
              Now that you know how to prepare, head over to our Materials Hub
              to select your products and schedule your delivery.
            </p>
            <a href="/#materials" className="inline-flex items-center justify-center bg-brand-orange hover:bg-brand-orangeHover text-white px-8 py-4 rounded-xl font-extrabold text-lg transition-all shadow-[0_0_20px_rgba(255,140,50,0.3)] hover:shadow-[0_0_25px_rgba(255,140,50,0.5)]" data-id="element-1953">
              Browse Materials Hub
              <ArrowRight className="w-5 h-5 ml-2" data-id="element-1954" />
            </a>
          </div>
        </motion.section>
      </div>
    </div>;
}